import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { ILead } from "../../../models/Lead";
import { getLeadLocation } from "../../../utils/getLeadLocation";
import StatusTabs, { StatusTabItem } from "../../shared/Tabs/StatusTabs";
import LeadContactAndVehicleTab from "./LeadContactAndVehicleTab";
import LeadNotesTab from "./LeadNotesTab";
import LeadPaymentDetailsTab from "./LeadPaymentDetailsTab";
import LeadQuoteLocationTab from "./LeadQuoteLocationTab";

const LEAD_DETAILS_ACTIVE_TAB_STORAGE_KEY = "lead-details-active-tab";

export default function LeadDetailsTabs({ lead }: { lead: ILead }) {
  const { t } = useTranslation("leads");
  const tabs = useMemo<StatusTabItem[]>(() => {
    const contactComplete = !!(lead.contact?.fullname && lead.contact?.telephone1);
    const paymentComplete = lead.paymentStatus !== 0 && lead.paymentStatus !== 3;
    const notesComplete = !!(lead.officeNotes || lead.driverNotes || lead.yardNotes || lead.vehicle?.notes);

    const vehicleAddressComplete = !!getLeadLocation(lead)?.line1;

    const items: StatusTabItem[] = [
      {
        key: "contact-and-vehicle",
        title: t("Contact & Vehicle Details"),
        status: contactComplete && vehicleAddressComplete ? "complete" : "incomplete",
        children: <LeadContactAndVehicleTab lead={lead} />,
      },
    ];

    items.push(
      {
        key: "payment-details",
        title: t("Payment Details"),
        status: paymentComplete ? "complete" : "incomplete",
        children: <LeadPaymentDetailsTab lead={lead} />,
      },
      {
        key: "notes",
        title: t("Notes"),
        status: notesComplete ? "complete" : "incomplete",
        children: <LeadNotesTab lead={lead} />,
      },
    );

    // Historical quote location only exists for some leads; show its tab last and muted
    // since it's a read-only historical record rather than an actionable detail.
    if (lead.quoteLocationId) {
      items.push({
        key: "quote-location",
        title: t("Quote Location"),
        status: "muted",
        children: <LeadQuoteLocationTab lead={lead} />,
      });
    }

    return items;
  }, [lead, t]);

  const defaultTabKey = useMemo(() => {
    if (typeof window === "undefined") return "contact-and-vehicle";

    const savedTabKey = window.localStorage.getItem(LEAD_DETAILS_ACTIVE_TAB_STORAGE_KEY);
    const isSavedTabAvailable = !!savedTabKey && tabs.some((tab) => tab.key === savedTabKey);

    return isSavedTabAvailable ? savedTabKey : "contact-and-vehicle";
  }, [tabs]);

  const handleTabChange = (tabKey: string) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LEAD_DETAILS_ACTIVE_TAB_STORAGE_KEY, tabKey);
  };

  return <StatusTabs tabs={tabs} defaultTabKey={defaultTabKey} onTabChange={handleTabChange} />;
}
